/**
 * StagnantOKRList Component
 * Displays OKRs with no recent activity
 *
 * Story 4.3: Stagnant OKR Detection
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Clock, User, Calendar, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../../common';
import type { StagnantOKRsData, StagnantOKRItem } from '../../../services/reportApi';
import { format } from 'date-fns';

export interface StagnantOKRListProps {
  /** Stagnant OKRs data */
  data: StagnantOKRsData;
  /** Loading state */
  isLoading?: boolean;
  /** Maximum items to show initially */
  initialLimit?: number;
  /** Click handler for OKR */
  onOKRClick?: (okrId: string) => void;
  /** Click handler for owner */
  onOwnerClick?: (userId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Get stagnation severity
 */
function getSeverity(days: number): { label: string; color: string; bg: string } {
  if (days >= 30) {
    return { label: 'Critical', color: 'text-red-700', bg: 'bg-red-100' };
  }
  if (days >= 21) {
    return { label: 'High', color: 'text-orange-700', bg: 'bg-orange-100' };
  }
  return { label: 'Moderate', color: 'text-amber-700', bg: 'bg-amber-100' };
}

/**
 * Loading skeleton
 */
const StagnantSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="h-6 bg-stone-200 rounded w-36" />
      <div className="h-6 bg-stone-200 rounded w-24" />
    </div>
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 border border-stone-200 rounded-lg">
          <div className="h-5 bg-stone-200 rounded w-3/4 mb-2" />
          <div className="h-4 bg-stone-200 rounded w-1/2 mb-2" />
          <div className="flex gap-4">
            <div className="h-4 bg-stone-200 rounded w-24" />
            <div className="h-4 bg-stone-200 rounded w-32" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Individual stagnant OKR item
 */
const OKRItem: React.FC<{
  okr: StagnantOKRItem;
  onOKRClick?: (okrId: string) => void;
  onOwnerClick?: (userId: string) => void;
}> = ({ okr, onOKRClick, onOwnerClick }) => {
  const severity = getSeverity(okr.daysSinceActivity);
  const lastActivityDate = okr.lastActivityDate ? new Date(okr.lastActivityDate) : null;

  return (
    <div className="p-4 border border-stone-200 rounded-lg hover:border-stone-300 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Title */}
          <button
            onClick={() => onOKRClick?.(okr.okrId)}
            className="text-left font-medium text-stone-900 hover:text-indigo-600 transition-colors truncate block w-full"
          >
            {okr.title}
          </button>

          {/* Owner info */}
          <button
            onClick={() => onOwnerClick?.(okr.ownerId)}
            className="flex items-center gap-1 mt-1 text-sm text-stone-500 hover:text-stone-700 transition-colors"
          >
            <User className="w-3 h-3" />
            <span>{okr.ownerName}</span>
            {okr.department && (
              <span className="text-stone-400">({okr.department})</span>
            )}
          </button>
        </div>

        {/* Severity badge */}
        <span className={clsx(
          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
          severity.bg,
          severity.color
        )}>
          {okr.daysSinceActivity}d inactive
        </span>
      </div>

      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm text-stone-500">
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          Progress: <span className="font-medium text-stone-700">{okr.progress}%</span>
        </span>

        {lastActivityDate && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            Last: {format(lastActivityDate, 'MMM d, yyyy')}
          </span>
        )}

        <span className="text-stone-400">
          via {okr.lastActivityType}
        </span>
      </div>
    </div>
  );
};

/**
 * Stagnant OKR List Component
 */
export const StagnantOKRList: React.FC<StagnantOKRListProps> = ({
  data,
  isLoading = false,
  initialLimit = 5,
  onOKRClick,
  onOwnerClick,
  className,
}) => {
  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <StagnantSkeleton />
      </Card>
    );
  }

  const { okrs, total, avgDaysSinceActivity } = data;
  const displayedOKRs = showAll ? okrs : okrs.slice(0, initialLimit);
  const hasMore = okrs.length > initialLimit;

  // Group by severity
  const criticalCount = okrs.filter((o) => o.daysSinceActivity >= 30).length;
  const highCount = okrs.filter((o) => o.daysSinceActivity >= 21 && o.daysSinceActivity < 30).length;
  const moderateCount = okrs.filter((o) => o.daysSinceActivity < 21).length;

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">Stagnant OKRs</h3>
          <p className="text-sm text-stone-500">
            {total} OKRs with no recent activity
            {avgDaysSinceActivity > 0 && (
              <span> (avg {avgDaysSinceActivity.toFixed(0)} days)</span>
            )}
          </p>
        </div>

        {/* Severity summary */}
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              {criticalCount} 30d+
            </span>
          )}
          {highCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
              {highCount} 21d+
            </span>
          )}
          {moderateCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              {moderateCount} 14d+
            </span>
          )}
        </div>
      </div>

      {/* OKR List */}
      {okrs.length === 0 ? (
        <div className="text-center py-8 text-stone-500">
          <Clock className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No stagnant OKRs found</p>
          <p className="text-sm mt-1">All OKRs have recent activity!</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {displayedOKRs.map((okr) => (
              <OKRItem
                key={okr.okrId}
                okr={okr}
                onOKRClick={onOKRClick}
                onOwnerClick={onOwnerClick}
              />
            ))}
          </div>

          {/* Show more button */}
          {hasMore && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-1"
            >
              {showAll ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show {okrs.length - initialLimit} more
                </>
              )}
            </button>
          )}
        </>
      )}

      {/* Tip */}
      {okrs.length > 0 && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Consider reaching out to owners of stagnant OKRs to ensure they have the support needed to make progress.
          </p>
        </div>
      )}
    </Card>
  );
};

export default StagnantOKRList;
